import React, { useEffect, useState } from 'react';
import { DefaultButton, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, IDropdownOption, Label } from '@fluentui/react';

import { IChoiceField, IField, IForwardRule, IForwardRuleUor, ITextField, ITitolario } from '../../shared/interfaces';
import DynamicForm from '../DynamicForm/DynamicForm';
import { useBoolean } from '@fluentui/react-hooks';
import { RootState } from '../../store';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { ForwardRuleUor_Type, Indici_FormMode } from '../../shared/enums';
import AppUtilities from '../../shared/Utilities/appUtilities';
import CircleLoader from '../Loader/CircleLoader/CircleLoader';
import uorsService from '../../services/uors.service';
import fieldsService from '../../services/fields.service';
import FilingPlanTree from '../FilingPlan/FilingPlanTree';


const mapState = (state: RootState) => ({

})

const mapDispatch = {

}
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>

export interface AddEditForwardRuleProps {
    loading?: boolean
    extraCssClass?: string
    selectedForwardRule?: Partial<IForwardRule>,
    panelHeaderText: string
    panelIsOpen: boolean
    targetFieldIsReadOnly?: boolean,
    hideTitolarioField?: boolean,
    onSubmit: (forwardRule: Partial<IForwardRule>) => void
    onDismiss: () => void

}

type Props = AddEditForwardRuleProps & PropsFromRedux

const AddEditForwardRule: React.FunctionComponent<Props> = (props) => {

    const selectCurrentAoo = (state: RootState) => state.sessionReducer.currentAoo;
    const currentAoo = useSelector(selectCurrentAoo);

    const loading = props.loading !== undefined ? props.loading : false;

    const [messageBarIsVisible, { setTrue: showMessageBar, setFalse: dismissMessageBar }] = useBoolean(false);
    const [messageBarType, setMessageBarType] = useState<MessageBarType>();
    const [messageBarText, setMessageBarText] = useState<string>('');
    const [formFields, setFormFields] = useState<IField[]>([]);
    const [targetFieldOptions, setTargetFieldOptions] = useState<IDropdownOption[]>([]);
    const [uorOptions, setUorOptions] = useState<IDropdownOption[]>([]);

    const [selectedTitolario, setSelectedTitolario] = useState<Partial<ITitolario> | undefined>(undefined);

    // Carica le opzioni una sola volta (o quando cambia l'AOO corrente).
    // Il completamento del caricamento scatena l'effetto successivo, che reimposta i campi.
    useEffect(() => {
        if (!currentAoo) return;
        getTargetFieldOptions();
        getUorOptions();
    }, [currentAoo?.id]);

    // Reimposta il form ogni volta che il panel viene aperto o quando cambiano
    // i dati da cui dipende (elemento selezionato, opzioni, modalità readOnly).
    // La guardia evita il reset durante la chiusura, che causerebbe valori stantii
    // se selectedForwardRule non è ancora aggiornato al momento della chiusura.
    useEffect(() => {
        if (!props.panelIsOpen) return;
        dismissMessageBar();
        resetFields();
    }, [props.panelIsOpen, props.selectedForwardRule, props.targetFieldIsReadOnly, targetFieldOptions, uorOptions]);

    const getTargetFieldOptions = () => {
        if (currentAoo) {
            fieldsService.getFieldsByViewName(currentAoo.id, 'Campi protocollo')
                .then((fields) => {

                    let _fields = AppUtilities.fixFieldsForm(Indici_FormMode.CreateForwardRule, fields);
                    let options: IDropdownOption[] = _fields.map(el => { return { text: el.displayName, key: el.internalName, id: el.internalName, data: el } })

                    // L'aggiornamento di targetFieldOptions scatena il re-render e il form
                    // viene aggiornato con le nuove opzioni alla successiva apertura del panel.
                    setTargetFieldOptions(options);


                });
        }
    }


    const getUorOptions = () => {
        if (currentAoo) {
            uorsService.getOrganigrammaAoo(currentAoo.id)
                .then(uors => {
                    let organigrammaFlat: IDropdownOption[] = [];
                    // Rimappa le UOR nel formato IDropdownOption[] per le dropdown multi-select.
                    remapOrganigrammaNodes(uors, organigrammaFlat);

                    // L'aggiornamento di uorOptions scatena il re-render e il form
                    // viene aggiornato con le nuove opzioni alla successiva apertura del panel.
                    setUorOptions(organigrammaFlat);
                });
        }
    }


    const remapOrganigrammaNodes = (uorList, organigrammaFlat: IDropdownOption[]) => {
        uorList.forEach(uorItem => {
            organigrammaFlat.push({
                key: uorItem.id,
                id: uorItem.id,
                data: {
                    id: uorItem.id,
                    nome: uorItem.nome,
                    sigla: uorItem.sigla,
                },
                text: uorItem.nome
            });
            remapOrganigrammaNodes(uorItem.children, organigrammaFlat)

        });
    }

    const resetFields = () => {
        // Estrae gli UorId preselezionati per tipo come array di chiavi numeriche,
        // che è il formato atteso da DynamicForm per i dropdown multi-select.
        const uorInoltro: (number | undefined)[] = props.selectedForwardRule?.uorDestinazioni
            ?.filter(u => u.type === ForwardRuleUor_Type.Inoltro)
            .map(u => u.uorId) ?? [];

        const uorCC: (number | undefined)[] = props.selectedForwardRule?.uorDestinazioni
            ?.filter(u => u.type === ForwardRuleUor_Type.CopiaConoscenza)
            .map(u => u.uorId) ?? [];

        setSelectedTitolario(props.selectedForwardRule?.titolario);

        const formsInitialFields: IField[] = [
            {
                displayName: "Campo",
                internalName: "fieldInternalName",
                controlType: "dropdown",
                required: true,
                readonly: props.targetFieldIsReadOnly,
                disabled: false,
                system: false,
                internalValue: props.selectedForwardRule?.fieldInternalName ?? '',
                valid: false,
                touched: false,
                multi: false,
                options: targetFieldOptions
            } as IChoiceField,
            {
                displayName: "Valore",
                internalName: "fieldInternalValue",
                controlType: "textbox",
                required: false,
                readonly: props.targetFieldIsReadOnly,
                disabled: false,
                system: false,
                internalValue: props.selectedForwardRule?.fieldInternalValue ?? '',
                valid: false,
                touched: false,
            } as ITextField,
            {
                displayName: "UOR in Inoltro",
                internalName: "uorInoltro",
                controlType: "dropdown",
                required: false,
                readonly: false,
                disabled: false,
                system: false,
                internalValue: uorInoltro,
                valid: false,
                touched: false,
                multi: true,
                options: uorOptions
            } as IChoiceField,
            {
                displayName: "UOR in Copia Conoscenza",
                internalName: "uorCC",
                controlType: "dropdown",
                // Non obbligatoria: una regola può non avere UOR in copia conoscenza.
                required: false,
                readonly: false,
                disabled: false,
                system: false,
                internalValue: uorCC,
                valid: false,
                touched: false,
                multi: true,
                options: uorOptions
            } as IChoiceField
        ];

        setFormFields(formsInitialFields);
    }


    // Raccoglie i valori della form, costruisce l'oggetto IForwardRule e chiama onSubmit.
    const addEditForwardRuleSubmit = () => {
        if (!checkForwardRuleFormValidity() || !currentAoo) return;

        const fieldInternalName: string = formFields.find(el => el.internalName === 'fieldInternalName')?.internalValue;
        const fieldInternalValue: string = formFields.find(el => el.internalName === 'fieldInternalValue')?.internalValue;
        const uorInoltroKeys: number[] = formFields.find(el => el.internalName === 'uorInoltro')?.internalValue ?? [];
        const uorCCKeys: number[] = formFields.find(el => el.internalName === 'uorCC')?.internalValue ?? [];

        const newForwardRule: Partial<IForwardRule> = {
            id: props.selectedForwardRule?.id,
            aooId: currentAoo.id,
            fieldInternalName,
            fieldInternalValue,
            titolarioId: selectedTitolario?.id,
            uorDestinazioni: [
                ...uorInoltroKeys.map(uorId => ({ uorId, type: ForwardRuleUor_Type.Inoltro } as IForwardRuleUor)),
                ...uorCCKeys.map(uorId => ({ uorId, type: ForwardRuleUor_Type.CopiaConoscenza } as IForwardRuleUor))
            ]
        };

        props.onSubmit(newForwardRule);
    }

    const addEditForwardRuleDismiss = () => {
        props.onDismiss();
    }

    const checkForwardRuleFormValidity = (): boolean => {
        const updatedFields = AppUtilities.checkFormFieldsValidity(formFields);
        const fieldsValid = !updatedFields.find(el => !el.valid);
        setFormFields(updatedFields);

        if (!fieldsValid) {
            setMessageBarText('Compilare correttamente i campi');
            setMessageBarType(MessageBarType.error);
            showMessageBar();
            return false;
        }

        const uorInoltroKeys: number[] = updatedFields.find(el => el.internalName === 'uorInoltro')?.internalValue ?? [];
        const uorCCKeys: number[] = updatedFields.find(el => el.internalName === 'uorCC')?.internalValue ?? [];

        // Una stessa UOR non può comparire sia in Inoltro che in Copia Conoscenza.
        const duplicateUorIds = uorInoltroKeys.filter(id => uorCCKeys.includes(id));
        if (duplicateUorIds.length > 0) {
            setMessageBarText('Una o più UOR sono presenti sia in Inoltro che in Copia Conoscenza');
            setMessageBarType(MessageBarType.error);
            showMessageBar();
            return false;
        }

        // Almeno uno tra UOR Inoltro, UOR CC e Titolario deve essere valorizzato.
        const hasDestination = uorInoltroKeys.length > 0 || uorCCKeys.length > 0 || selectedTitolario?.id != null;
        if (!hasDestination) {
            setMessageBarText('Compilare almeno uno tra UOR Inoltro, UOR Copia Conoscenza e Titolario');
            setMessageBarType(MessageBarType.error);
            showMessageBar();
            return false;
        }

        dismissMessageBar();
        return true;
    }


    const inputChangedHandler = (newField: IField) => {

        const updatedFields = [...formFields];
        let itemIndex = updatedFields.findIndex(el => el.internalName === newField.internalName);

        updatedFields[itemIndex] = { ...newField };

        setFormFields(updatedFields);
    }

    const onSelectFilingPlanHandler = (selectedKey) => {

        setSelectedTitolario(selectedKey);
    }


    const onRenderLateralPanelFooterContent = () => {
        return (
            <React.Fragment>
                <PrimaryButton
                    styles={{ root: { marginRight: 8 } }}
                    text="Salva"
                    onClick={addEditForwardRuleSubmit}

                />
                <DefaultButton
                    onClick={addEditForwardRuleDismiss}
                    text="Annulla"
                />
            </React.Fragment>
        );
    }


    return (
        <React.Fragment>
            <Panel
                key='addEditUserPanel'
                isLightDismiss
                isOpen={props.panelIsOpen}
                type={PanelType.custom}
                customWidth={'600px'}
                headerText={props.panelHeaderText}
                focusTrapZoneProps={{
                    isClickableOutsideFocusTrap: true,
                    forceFocusInsideTrap: true,
                }}
                onDismiss={addEditForwardRuleDismiss}
                onRenderFooterContent={onRenderLateralPanelFooterContent}
                // Il footer rimane in posizione fissa in fondo al panel, non scorre con il contenuto.
                isFooterAtBottom={false}
            >
                {messageBarIsVisible &&
                    <MessageBar
                        messageBarType={messageBarType}
                        isMultiline={false}
                        onDismiss={dismissMessageBar}
                        dismissButtonAriaLabel="Close">{messageBarText}</MessageBar>
                }

                <CircleLoader loaderClass='loader-large' loading={loading} />

                <div className={"flex flex-column position-relative overflow-hidden"}>

                    <div className={`position-relative my-scroll-div`}>

                        <DynamicForm
                            fields={formFields}
                            onInputChangedHandler={(newField) => { inputChangedHandler(newField) }}
                        />
                        {!props.hideTitolarioField && (
                            <div className={'titolario field'} >
                            <Label className='field-label' htmlFor={"Titolario"} >Titolario</Label>
                            <FilingPlanTree
                                key={"Titolario"}
                                readOnlyMode={false}
                                selectedFilingPlanKey={selectedTitolario?.label}
                                onSelectFilingPlanHandler={(selected) => onSelectFilingPlanHandler(selected)}
                            />
                        </div>
                        )}
                    </div>
                </div>


            </Panel>



        </React.Fragment>

    );
}

export default connector(AddEditForwardRule);
